import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { isProduction } from "../../utils/checkEnv";
import { loadMetaPixel, trackMeta } from "../../utils/metaPixel";
import ReactGA from "react-ga";
import TagManager from "react-gtm-module";
const tagManagerArgs = {
  gtmId: "GTM-PFRB7XB",
};

/**
 * GA4 property shared with the marketing site. Using the same measurement id on
 * both hosts is what lets a single report follow a visit from savings.club into
 * this flow: contractflow.savings.club is a subdomain, so GA keeps the session.
 *
 * The GTM container above does not carry a GA4 tag today (checked against
 * production), so this adds the only one, with no risk of double counting.
 */
const GA4_MEASUREMENT_ID = "G-2S05RY9J6H";
const GA4_SCRIPT_ID = "ga4-loader";

const loadGa4 = () => {
  if (document.getElementById(GA4_SCRIPT_ID)) return;

  const script = document.createElement("script");
  script.id = GA4_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  // Routing here is client side, so page_view is sent per route below rather
  // than once on load.
  window.gtag("config", GA4_MEASUREMENT_ID, { send_page_view: false });
};

export const useAnalytics = () => {
  const { pathname, search } = useLocation();
  const { currentStep } = useSelector((state) => state.lead);

  useEffect(() => {
    const isProd = isProduction();
    if (isProd) {
      TagManager.initialize(tagManagerArgs);
      ReactGA.initialize("UA-222921285-1");
      ReactGA.pageview(window.location.pathname + window.location.search);
      loadGa4();
      // The Pixel only ever saw the lead on savings.club. Loading it here is
      // what lets Meta attribute a signed contract to the ad that paid for it.
      loadMetaPixel();
    }
  }, []);

  // GA4 counts URLs, so this stays keyed on the route. Firing it per flow step
  // would report several page_views for one path and break every page report.
  useEffect(() => {
    if (!isProduction() || !window.gtag) return undefined;

    // Routes that exist only to redirect (/ hands straight over to /simulator)
    // would otherwise report as pageviews of their own. Waiting a beat lets the
    // cleanup drop them, so only a route the visitor actually lands on is sent.
    const timer = setTimeout(() => {
      window.gtag("event", "page_view", {
        page_path: pathname + search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }, 400);

    return () => clearTimeout(timer);
  }, [pathname, search]);

  /**
   * The Pixel counts screens, and a screen here is a route AND a step: the
   * flow's four steps (Start deal, Review offer, Sign deal, Welcome) all live
   * on one URL, driven by Redux rather than the router. Keyed on the route
   * alone the whole wizard reports as a single PageView; reported from each
   * step page instead, the screen the visitor arrives on reports twice, once
   * for the route and once for the step. One effect over both avoids both.
   *
   * The debounce is the same 400ms and earns its keep twice over here: a
   * resuming visitor is briefly on step 0 before the API answers with the step
   * they actually left off at, and the cleanup drops that phantom screen.
   */
  useEffect(() => {
    if (!isProduction()) return undefined;

    const timer = setTimeout(() => trackMeta("PageView"), 400);

    return () => clearTimeout(timer);
  }, [pathname, search, currentStep]);
};
